import React, { useState, useEffect, useMemo, useRef } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { NumberField } from "../../../shared/components/NumberField.jsx";
import { QuantityCalculatorSheet } from "../../../shared/components/QuantityCalculatorSheet.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { useItems } from "../../items/hooks/useItems.js";
import { itemInCompany } from "../../items/models/item.js";
import { useCompanyParties } from "../../customers/hooks/useCompanyParties.js";
import { useCompanies } from "../../companies/hooks/useCompanies.js";
import {
  PURCHASE_TYPES,
  TCS_RATES,
  calculatePurchaseLineItem,
  calculatePurchaseTotals
} from "../models/purchase.js";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { useActiveCompany, useCompanyLock } from "../../../shared/context/ActiveCompanyContext.jsx";
import { updatePurchase } from "../services/purchaseService.js";
import { diffPurchase } from "../../invoices/utils/invoiceEdit.js";

/**
 * Record a new purchase bill, or edit a saved one (editingPurchase): the same 3 steps, filled in.
 * When editing, the vendor is locked once payments are recorded against the bill.
 */
export function PurchaseCreateModal({ isOpen, onClose, onPurchaseCreated, initialCompanyId, editingPurchase = null, onPurchaseUpdated }) {
  const isEditing = !!editingPurchase;
  const { confirmDanger } = useDangerConfirm();
  const { items, refetch: refetchItems } = useItems();
  // Only the working company's vendors (header)
  const { customers, refetch: refetchCustomers, dueOf } = useCompanyParties();
  const { companies } = useCompanies();
  // New purchases are booked under the company picked in the header (locked while this is open)
  const { activeCompany, activeCompanyId } = useActiveCompany();
  useCompanyLock(isOpen);

  // Wizard Step: 1 = Bill Info & Vendor, 2 = Items & Stock-In, 3 = Review & Submit
  const [step, setStep] = useState(1);

  // Form State
  const [vendorBillNo, setVendorBillNo] = useState("");
  const [billDate, setBillDate] = useState(new Date().toISOString().split("T")[0]);
  const [billType, setBillType] = useState(PURCHASE_TYPES.GST);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorSearchTerm, setCustomerSearchTerm] = useState("");
  const [lineItems, setLineItems] = useState([]);
  const [discountInput, setDiscountInput] = useState(0);
  const [tcsRate, setTcsRate] = useState(0);
  const [tcsAmountInput, setTcsAmountInput] = useState(null); // null = rate × (taxable + GST)
  const [paymentOption, setPaymentOption] = useState("Unpaid"); // 'Unpaid' | 'Paid'

  // Item Picker
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [qtyCalcIndex, setQtyCalcIndex] = useState(null); // line whose qty is being added up
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current && editingPurchase) {
      // Edit: fill every step from the saved bill and start on Items (the usual fix)
      const bill = editingPurchase;
      setSelectedCompany(companies.find((c) => c.id === bill.companyId) || (bill.companyId ? { id: bill.companyId, name: bill.companyName } : null));
      setVendorBillNo(bill.vendorBillNo || "");
      setBillDate(bill.billDate || new Date().toISOString().split("T")[0]);
      setBillType(bill.billType === PURCHASE_TYPES.NORMAL || bill.isNonGst ? PURCHASE_TYPES.NORMAL : PURCHASE_TYPES.GST);
      setSelectedVendor(customers.find((c) => c.id === bill.vendorId) || {
        id: bill.vendorId, name: bill.vendorName, gstin: bill.vendorGstin || "", mobile: bill.vendorPhone || "",
        city: bill.vendorCity || "", state: bill.vendorState || "", stateCode: bill.vendorStateCode || "", partyType: "vendor"
      });
      setLineItems((bill.lineItems || []).map((li) => ({ ...li })));
      setDiscountInput(Number(bill.discountAmount || 0));
      setTcsRate(Number(bill.tcsRate || 0));
      setTcsAmountInput(bill.tcsManual ? Number(bill.tcsAmount || 0) : null);
      const paid = Number(bill.paid || 0);
      const payments = (bill.paymentHistory || []).filter((h) => !h.voided && Number(h.amount || 0) > 0);
      setPaymentOption(!payments.length && paid > 0 && paid >= Number(bill.grandTotal || 0) - 0.005 ? "Paid" : "Unpaid");
      setError("");
      setStep(2);
      if (refetchItems) refetchItems();
    } else if (isOpen && !prevIsOpenRef.current) {
      setStep(1);
      setSelectedCompany(activeCompany || (initialCompanyId && companies.find((c) => c.id === initialCompanyId)) || null);
      setVendorBillNo("");
      setBillDate(new Date().toISOString().split("T")[0]);
      setBillType(PURCHASE_TYPES.GST);
      setSelectedVendor(null);
      setLineItems([]);
      setDiscountInput(0);
      setTcsRate(0);
      setTcsAmountInput(null);
      setPaymentOption("Unpaid");
      setError("");
      if (refetchItems) refetchItems();
      if (refetchCustomers) refetchCustomers();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, activeCompany, companies]);

  // Filter vendors (partyType is 'vendor' or 'both')
  const vendorList = useMemo(() => {
    const list = customers.filter(
      (c) => c.partyType === "vendor" || c.partyType === "both" || !c.partyType
    );
    if (!vendorSearchTerm.trim()) return list;
    const term = vendorSearchTerm.toLowerCase();
    return list.filter(
      (v) =>
        (v.name || "").toLowerCase().includes(term) ||
        (v.city || "").toLowerCase().includes(term) ||
        (v.gstin || "").toLowerCase().includes(term) ||
        (v.mobile || "").includes(term)
    );
  }, [customers, vendorSearchTerm]);

  // Filter items in catalog (goods only, since inward stock is physical)
  const catalogItems = useMemo(() => {
    const companyId = selectedCompany?.id || activeCompanyId;
    const goods = items.filter((i) => i.itemType !== "service" && itemInCompany(i, companyId));
    if (!itemSearchTerm.trim()) return goods;
    const term = itemSearchTerm.toLowerCase();
    return goods.filter(
      (i) =>
        (i.itemName || i.name || "").toLowerCase().includes(term) ||
        (i.gstCode || i.sku || "").toLowerCase().includes(term)
    );
  }, [items, itemSearchTerm, selectedCompany, activeCompanyId]);

  // Compute live financial totals
  const totals = useMemo(() => {
    return calculatePurchaseTotals(lineItems, selectedVendor, discountInput, billType, selectedCompany, { rate: tcsRate, amount: tcsAmountInput });
  }, [lineItems, selectedVendor, discountInput, billType, selectedCompany, tcsRate, tcsAmountInput]);

  // Edit: what saving will change (same calculation the save uses)
  const editChange = useMemo(() => {
    if (!isEditing) return null;
    return diffPurchase(editingPurchase, {
      grandTotal: totals.grandTotal,
      lineItems,
      vendorId: selectedVendor?.id,
      vendorName: selectedVendor?.name,
      billDate,
      paidInFull: paymentOption === "Paid"
    });
  }, [isEditing, editingPurchase, totals.grandTotal, lineItems, selectedVendor, billDate, paymentOption]);
  const vendorLocked = !!editChange?.customerLocked;

  // How many cart lines each item has (the same item can appear on several lines)
  const cartCountByItem = useMemo(() => {
    const counts = {};
    lineItems.forEach((li) => {
      if (li.itemId) counts[li.itemId] = (counts[li.itemId] || 0) + 1;
    });
    return counts;
  }, [lineItems]);

  // Add item to cart. Every tap adds a separate line, so the same item can be inwarded with different qty/rate.
  const handleAddItem = (item) => {
    const isNonGst = billType === PURCHASE_TYPES.NORMAL;
    const initialCost = Number(item.costPrice || item.basicPrice || 0);
    setLineItems((prev) => [...prev, calculatePurchaseLineItem(item, 1, initialCost, isNonGst)]);
  };

  // Modify item quantity directly
  const handleQtyChange = (index, newQty, qtyBreakdown = undefined) => {
    const isNonGst = billType === PURCHASE_TYPES.NORMAL;
    const parsed = parseFloat(newQty);
    const qty = isNaN(parsed) ? 1 : Math.max(0.001, parsed);
    const target = lineItems[index];
    if (!target) return;
    const originalItem = items.find((i) => i.id === target.itemId) || { ...target, id: target.itemId }; // keep the item link if the catalogue is still loading
    const updated = [...lineItems];
    const resolvedBreakdown = qtyBreakdown !== undefined
      ? qtyBreakdown
      : (Array.isArray(target.qtyBreakdown) && Math.abs(target.qtyBreakdown.reduce((a, b) => a + b, 0) - qty) < 0.001 ? target.qtyBreakdown : null);
    updated[index] = calculatePurchaseLineItem(originalItem, qty, target.rate, isNonGst, resolvedBreakdown);
    setLineItems(updated);
  };

  const handleQtyCalcApply = (index, total, breakdown) => {
    handleQtyChange(index, total, breakdown);
  };

  // Modify item rate (vendor cost price)
  const handleRateChange = (index, newRate) => {
    const isNonGst = billType === PURCHASE_TYPES.NORMAL;
    const parsed = parseFloat(newRate);
    const rate = isNaN(parsed) ? 0 : Math.max(0, parsed);
    const target = lineItems[index];
    if (!target) return;
    const originalItem = items.find((i) => i.id === target.itemId) || { ...target, id: target.itemId }; // keep the item link if the catalogue is still loading
    const updated = [...lineItems];
    updated[index] = calculatePurchaseLineItem(originalItem, target.qty, rate, isNonGst);
    setLineItems(updated);
  };

  const handleRemoveLine = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    setError("");
    if (step === 1) {
      if (!vendorBillNo.trim()) {
        setError("Please enter the Vendor's Bill / Invoice Number.");
        return;
      }
      if (!selectedVendor) {
        setError("Please select the Vendor / Supplier who sent this bill.");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (lineItems.length === 0) {
        setError("Please add at least one item to inward stock.");
        return;
      }
      setStep(3);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const isPaid = paymentOption === "Paid";
      const payload = {
        vendorBillNo: vendorBillNo.trim(),
        billDate,
        billType, // 'GST' or 'Normal'
        companyId: selectedCompany?.id || "",
        companyName: selectedCompany?.name || "Main Company",
        vendorId: selectedVendor.id,
        vendorName: selectedVendor.name,
        vendorGstin: selectedVendor.gstin || "",
        vendorPhone: selectedVendor.mobile || "",
        vendorCity: selectedVendor.city || "",
        vendorState: selectedVendor.state || "Tamil Nadu",
        vendorStateCode: selectedVendor.stateCode || "33",
        lineItems,
        subtotal: totals.subtotal,
        discountAmount: totals.discountAmount,
        taxableAmount: totals.taxableAmount,
        gstTotal: totals.gstTotal,
        cgst: totals.cgst,
        sgst: totals.sgst,
        igst: totals.igst,
        isInterState: totals.isInterState,
        isNonGst: totals.isNonGst,
        tcsRate: totals.tcsRate,
        tcsAmount: totals.tcsAmount,
        tcsManual: totals.tcsManual,
        roundOff: totals.roundOff,
        grandTotal: totals.grandTotal,
        paid: isPaid ? totals.grandTotal : 0,
        balance: isPaid ? 0 : totals.grandTotal,
        paymentStatus: isPaid ? "Paid" : "Unpaid"
      };

      if (isEditing) {
        if (editChange?.receipts.length) {
          const ok = await confirmDanger({
            title: `Save changes to bill ${vendorBillNo}?`,
            message: `${editChange.summary.join("\n")}\n\nPayments already recorded (₹${editChange.recorded.toLocaleString("en-IN")}) stay as they are.`,
            confirmLabel: "Save changes"
          });
          if (!ok) return;
        }
        await updatePurchase(editingPurchase.id, payload, {
          expectedUpdatedAt: editingPurchase.updatedAt,
          paidInFull: paymentOption === "Paid"
        });
        if (onPurchaseUpdated) await onPurchaseUpdated();
        if (refetchItems) refetchItems();
      } else {
        await onPurchaseCreated(payload);
      }
      onClose();
    } catch (err) {
      console.error("Failed to save purchase bill:", err);
      setError(err.message || "Failed to record purchase bill.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing
        ? `✏️ Edit purchase ${editingPurchase.vendorBillNo || ""} • ${step}/3`
        : step === 1 ? "1/3 Vendor & Bill Details" : step === 2 ? "2/3 Items & Inward Stock" : "3/3 Review & Save Bill"}
      size="lg"
    >
      <div className="purchase-create-flow">
        {error && (
          <div className="form-error" style={{ marginBottom: "1rem" }}>
            {error}
          </div>
        )}

        {/* STEP 1: BILL DETAILS & VENDOR PICKER */}
        {step === 1 && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
              <div>
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Vendor Bill # *
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. INV-9042, BILL-102"
                  value={vendorBillNo}
                  onChange={(e) => setVendorBillNo(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Bill Date *
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={billDate}
                  onChange={(e) => setBillDate(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Bill Type & booking company (the header company; locked) */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
              <div>
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  Bill Category
                </label>
                <select
                  value={billType}
                  onChange={(e) => setBillType(e.target.value)}
                  className="form-control"
                >
                  <option value={PURCHASE_TYPES.GST}>GST Tax Bill</option>
                  <option value={PURCHASE_TYPES.NORMAL}>Non-GST / Cash Bill</option>
                </select>
              </div>

              <div data-testid="booking-company">
                <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                  {isEditing ? "Company" : "Booked under"}
                </label>
                <div className="form-control" style={{ display: "flex", alignItems: "center", gap: 4, opacity: 0.9, cursor: "default" }} title={isEditing ? "A saved bill keeps its company" : "Switch the company in the header"}>
                  🏢 <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedCompany?.name || "—"}</span> 🔒
                </div>
              </div>
            </div>

            {vendorLocked && (
              <div style={{ marginTop: "1rem", background: "var(--bg-card)", padding: "0.75rem", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
                <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>{selectedVendor?.name} 🔒</div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: 4 }}>
                  Payments ({editChange.receipts.map((h) => h.paymentNo).filter(Boolean).join(", ") || "recorded"}) belong to this vendor. Void them first to change the vendor.
                </div>
              </div>
            )}

            {/* Select Vendor */}
            {!vendorLocked && (
            <div style={{ marginTop: "1rem" }}>
              <div style={{ fontSize: "0.85rem", fontWeight: "700", marginBottom: "0.4rem" }}>
                Select Vendor / Supplier *
              </div>
              <input
                type="text"
                className="form-control"
                placeholder="🔍 Search vendor by name, phone, city..."
                value={vendorSearchTerm}
                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                style={{ marginBottom: "0.5rem" }}
              />

              <div style={{ maxHeight: "200px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                {vendorList.map((v) => {
                  const isSelected = selectedVendor?.id === v.id;
                  return (
                    <div
                      key={v.id}
                      onClick={() => setSelectedVendor(v)}
                      style={{
                        padding: "0.65rem 0.75rem",
                        background: isSelected ? "rgba(99, 102, 241, 0.18)" : "var(--bg-card)",
                        border: isSelected ? "1.5px solid #818cf8" : "1px solid var(--border-subtle)",
                        borderRadius: "var(--radius-sm)",
                        cursor: "pointer"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: "600", fontSize: "0.9rem" }}>{v.name}</span>
                        {v.gstin && <Badge variant="info" style={{ fontSize: "0.65rem" }}>GST</Badge>}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "2px" }}>
                        {v.city || "Tamil Nadu"} • 📞 {v.mobile || "No phone"}
                        {dueOf(v) > 0.005 && <span style={{ color: "var(--warning)", fontWeight: 600 }}> • Payable ₹{dueOf(v).toLocaleString("en-IN")}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </div>
        )}

        {/* STEP 2: ITEMS SELECTION & STOCK-IN */}
        {step === 2 && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
              <span style={{ fontSize: "0.85rem", fontWeight: "600" }}>
                Inward Stock Items ({lineItems.length})
              </span>
              <span style={{ fontSize: "0.9rem", fontWeight: "700", color: "var(--accent-blue)" }}>
                Subtotal: ₹{totals.subtotal.toLocaleString("en-IN")}
              </span>
            </div>

            {/* Cart Items List */}
            {lineItems.length > 0 && (
              <div style={{ marginBottom: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {lineItems.map((li, idx) => (
                  <Card key={idx} style={{ padding: "0.75rem", marginBottom: "0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                          {li.itemName}
                          {(cartCountByItem[li.itemId] || 0) > 1 && (
                            <span style={{ marginLeft: "6px", fontSize: "0.7rem", fontWeight: "700", color: "var(--primary)" }}>
                              #{lineItems.slice(0, idx + 1).filter((x) => x.itemId === li.itemId).length} of {cartCountByItem[li.itemId]}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "2px" }}>
                          {billType === PURCHASE_TYPES.GST ? `${li.gstPercent}% GST` : "Non-GST Bill (0% Tax)"}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveLine(idx)}
                        style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", padding: "2px" }}
                        aria-label="Remove item"
                      >
                        ✕
                      </button>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.5rem", flexWrap: "wrap", gap: "0.5rem" }}>
                      {/* Qty Counter */}
                      <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                        <span style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>Qty:</span>
                        <NumberField
                          className=""
                          aria-label="Quantity"
                          min={0.001}
                          fallback={1}
                          value={li.qty}
                          onValueChange={(v) => handleQtyChange(idx, v)}
                          style={{
                            width: "70px",
                            textAlign: "center",
                            background: "var(--bg-input)",
                            border: "1.5px solid var(--accent-blue, #38bdf8)",
                            borderRadius: "var(--radius-sm)",
                            color: "var(--text-main)",
                            padding: "3px",
                            fontWeight: "700"
                          }}
                        />
                        <span style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>{li.unit || "pcs"}</span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          style={{ width: "30px", height: "30px", padding: "0", minHeight: "unset", fontSize: "0.95rem" }}
                          onClick={() => setQtyCalcIndex(idx)}
                          title="Add up quantities"
                          aria-label="Add up quantities"
                        >
                          🧮
                        </button>
                      </div>

                      {Array.isArray(li.qtyBreakdown) && li.qtyBreakdown.length > 1 && (
                        <div style={{ marginTop: "2px" }}>
                          <button
                            type="button"
                            onClick={() => setQtyCalcIndex(idx)}
                            style={{
                              background: "var(--primary-subtle, rgba(56, 189, 248, 0.12))",
                              border: "1px dashed var(--primary)",
                              borderRadius: "var(--radius-sm)",
                              color: "var(--primary)",
                              fontSize: "0.72rem",
                              fontWeight: "700",
                              cursor: "pointer",
                              padding: "2px 6px",
                              textAlign: "left"
                            }}
                            title="Click to view/edit partial quantity breakdown"
                          >
                            🧮 {li.qtyBreakdown.length} parts ({li.qtyBreakdown.join(" + ")})
                          </button>
                        </div>
                      )}

                      {/* Cost Rate */}
                      <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                        <span style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>Cost: ₹</span>
                        <NumberField
                          className=""
                          aria-label="Rate"
                          min={0}
                          fallback={0}
                          decimals={2}
                          value={li.rate}
                          onValueChange={(v) => handleRateChange(idx, v)}
                          style={{
                            width: "75px",
                            textAlign: "right",
                            background: "var(--bg-input)",
                            border: "1px solid var(--border-subtle)",
                            borderRadius: "var(--radius-sm)",
                            color: "var(--text-main)",
                            padding: "3px 4px",
                            fontWeight: "700"
                          }}
                        />
                      </div>

                      {/* Line Total */}
                      <div style={{ fontWeight: "700", fontSize: "0.95rem" }}>
                        ₹{Number(li.lineTotal).toLocaleString("en-IN")}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Catalog Search & Add */}
            <div style={{ marginTop: "0.75rem" }}>
              <div style={{ fontSize: "0.8rem", fontWeight: "700", marginBottom: "0.3rem" }}>
                Add Goods from Catalog
              </div>
              <input
                type="text"
                placeholder="Search items by name or code..."
                value={itemSearchTerm}
                onChange={(e) => setItemSearchTerm(e.target.value)}
                className="form-control"
                style={{ marginBottom: "0.5rem" }}
              />

              <div style={{ maxHeight: "160px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                {catalogItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleAddItem(item)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.5rem 0.75rem",
                      background: "var(--bg-card)",
                      borderRadius: "var(--radius-sm)",
                      cursor: "pointer",
                      border: "1px solid var(--border-subtle)"
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "0.85rem", fontWeight: "600" }}>{item.itemName || item.name}</div>
                      <div style={{ fontSize: "0.7rem", color: "var(--text-dim)" }}>
                        Current Stock: {item.stock || 0} {item.unit || "pcs"} • Cost: ₹{item.costPrice || 0}
                        {cartCountByItem[item.id] > 0 && (
                          <span style={{ color: "var(--primary)", fontWeight: "700" }}> • In cart ×{cartCountByItem[item.id]}</span>
                        )}
                      </div>
                    </div>
                    <span style={{ color: "var(--primary)", fontWeight: "700", fontSize: "1.1rem" }}>+</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: REVIEW & PAYMENT STATUS */}
        {step === 3 && (
          <div>
            <div style={{ background: "var(--bg-card)", padding: "0.85rem", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)", marginBottom: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ color: "var(--text-dim)" }}>Vendor</span>
                <strong>{selectedVendor?.name}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ color: "var(--text-dim)" }}>Vendor Bill #</span>
                <strong>{vendorBillNo}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ color: "var(--text-dim)" }}>Bill Date</span>
                <span>{billDate}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ color: "var(--text-dim)" }}>Inward Items</span>
                <span>{lineItems.length} item(s)</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", paddingTop: "6px", borderTop: "1px solid var(--border-subtle)", marginBottom: "4px" }}>
                <span style={{ color: "var(--text-dim)" }}>Subtotal</span>
                <span>₹{totals.subtotal.toLocaleString("en-IN")}</span>
              </div>
              {!totals.isNonGst && (totals.isInterState ? (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ color: "var(--text-dim)" }}>IGST</span>
                  <span>₹{totals.igst.toLocaleString("en-IN")}</span>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                    <span style={{ color: "var(--text-dim)" }}>CGST</span>
                    <span>₹{totals.cgst.toLocaleString("en-IN")}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                    <span style={{ color: "var(--text-dim)" }}>SGST</span>
                    <span>₹{totals.sgst.toLocaleString("en-IN")}</span>
                  </div>
                </>
              ))}

              {/* TCS charged by the vendor (on taxable + GST); amount editable to match their bill */}
              <div data-testid="tcs-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", marginBottom: "4px" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "var(--text-dim)" }}>
                  TCS
                  <select
                    aria-label="TCS rate"
                    className="form-control"
                    value={tcsRate}
                    onChange={(e) => { setTcsRate(Number(e.target.value)); setTcsAmountInput(null); }}
                    style={{ width: "auto", minWidth: "6.5rem", minHeight: "32px", padding: "4px 1.9rem 4px 0.6rem", backgroundPosition: "right 0.5rem center", backgroundSize: "1rem", fontSize: "0.82rem" }}
                  >
                    {TCS_RATES.map((r) => <option key={r} value={r}>{r ? `${r}%` : "No TCS"}</option>)}
                  </select>
                </span>
                {(tcsRate > 0 || tcsAmountInput !== null) && (
                  <span style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                    {tcsAmountInput !== null && (
                      <button
                        type="button"
                        onClick={() => setTcsAmountInput(null)}
                        title="Use rate × (taxable + GST)"
                        style={{ background: "none", border: "none", color: "var(--primary)", cursor: "pointer", fontSize: "0.75rem", padding: 0 }}
                      >
                        ↺ auto
                      </button>
                    )}
                    ₹
                    <NumberField
                      className=""
                      aria-label="TCS amount"
                      min={0}
                      fallback={0}
                      decimals={2}
                      value={totals.tcsAmount}
                      onValueChange={(v) => setTcsAmountInput(Number(v) || 0)}
                      style={{
                        width: "85px",
                        textAlign: "right",
                        background: "var(--bg-input)",
                        border: "1px solid var(--border-subtle)",
                        borderRadius: "var(--radius-sm)",
                        color: "var(--text-main)",
                        padding: "3px 4px",
                        fontWeight: "700"
                      }}
                    />
                  </span>
                )}
              </div>
              {(tcsRate > 0 || tcsAmountInput !== null) && (
                <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", textAlign: "right", marginBottom: "4px" }}>
                  on ₹{totals.tcsBase.toLocaleString("en-IN")} (taxable + GST){tcsAmountInput !== null ? " • typed" : ""}
                </div>
              )}

              {totals.roundOff !== 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ color: "var(--text-dim)" }}>Round Off</span>
                  <span>₹{totals.roundOff.toLocaleString("en-IN")}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "800", fontSize: "1.1rem", marginTop: "8px", paddingTop: "6px", borderTop: "1px solid var(--border-subtle)" }}>
                <span>Grand Total</span>
                <span style={{ color: "var(--accent-blue)" }}>₹{totals.grandTotal.toLocaleString("en-IN")}</span>
              </div>
            </div>

            {/* Edit: what saving changes */}
            {editChange && (
              <div data-testid="edit-changes" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", padding: "0.75rem 0.85rem", marginBottom: "1rem", fontSize: "0.82rem" }}>
                <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "0.4rem" }}>What changes</div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                  <span style={{ color: "var(--text-muted)" }}>Total</span>
                  <span style={{ fontWeight: 700 }}>₹{editChange.oldTotal.toLocaleString("en-IN")} → ₹{editChange.newTotal.toLocaleString("en-IN")}</span>
                </div>
                {editChange.newPaid > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>Paid</span>
                    <span>₹{editChange.newPaid.toLocaleString("en-IN")}{editChange.receipts.length ? ` (${editChange.receipts.map((h) => h.paymentNo).filter(Boolean).join(", ")})` : " (paid at billing)"} • Balance ₹{editChange.newBalance.toLocaleString("en-IN")}</span>
                  </div>
                )}
                {editChange.stock.map((st) => (
                  <div key={st.itemId} style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>Stock • {st.itemName}</span>
                    <span>{st.stockChange > 0 ? `+${st.stockChange} into stock` : `−${Math.abs(st.stockChange)} taken back out of stock`}</span>
                  </div>
                ))}
                {editChange.dues.map((d) => (
                  <div key={d.customerId} style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                    <span style={{ color: "var(--text-muted)" }}>Payable • {d.name}</span>
                    <span>{d.delta > 0 ? "+" : "−"}₹{Math.abs(d.delta).toLocaleString("en-IN")}</span>
                  </div>
                ))}
                {editChange.overReceived > 0 && (
                  <div className="form-error" style={{ marginTop: "0.6rem" }}>
                    ⚠ Paid ₹{editChange.newPaid.toLocaleString("en-IN")} is more than the new total ₹{editChange.newTotal.toLocaleString("en-IN")}.
                    Edit or void {editChange.receipts.map((h) => h.paymentNo).filter(Boolean).join(", ") || "the payment"} first (in this bill's payment history).
                    <div style={{ marginTop: "0.4rem" }}>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close & fix payment</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Status Option (when editing, only for bills without recorded payments) */}
            {!(editChange?.receipts.length) && (
            <div style={{ marginBottom: "1rem" }}>
              <label className="form-label" style={{ fontSize: "0.8rem", fontWeight: "700" }}>
                Payment Status to Vendor
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                <button
                  type="button"
                  className={`btn ${paymentOption === "Unpaid" ? "btn-primary" : "btn-secondary"}`}
                  onClick={() => setPaymentOption("Unpaid")}
                  style={{ minHeight: "36px" }}
                >
                  ⏳ Unpaid (Pay Later)
                </button>
                <button
                  type="button"
                  className={`btn ${paymentOption === "Paid" ? "btn-primary" : "btn-secondary"}`}
                  onClick={() => setPaymentOption("Paid")}
                  style={{ minHeight: "36px" }}
                >
                  ✅ Paid in Full
                </button>
              </div>
            </div>
            )}

            {!isEditing && (
            <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", background: "rgba(59, 130, 246, 0.08)", padding: "0.6rem", borderRadius: "var(--radius-sm)", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
              ℹ️ Saving this bill will <strong>automatically increase stock</strong> in Inventory for all {lineItems.length} items, and record audit movement logs.
            </div>
            )}
          </div>
        )}

        {/* Navigation Footer */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "1.25rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border-subtle)" }}>
          {step > 1 ? (
            <Button variant="secondary" onClick={() => setStep(step - 1)}>
              ← Back
            </Button>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          )}

          {step < 3 ? (
            <Button variant="primary" onClick={handleNext}>
              Next →
            </Button>
          ) : (
            <Button variant="primary" onClick={handleSubmit} disabled={submitting || editChange?.overReceived > 0}>
              {isEditing ? (submitting ? "Saving…" : "Save changes") : submitting ? "Saving & Inwarding Stock..." : "Confirm & Update Stock 🚀"}
            </Button>
          )}
        </div>
      </div>
    </BottomSheet>

    <QuantityCalculatorSheet
      isOpen={qtyCalcIndex !== null}
      onClose={() => setQtyCalcIndex(null)}
      itemName={lineItems[qtyCalcIndex]?.itemName}
      unit={lineItems[qtyCalcIndex]?.unit || "pcs"}
      initialRows={
        lineItems[qtyCalcIndex]?.qtyBreakdown ||
        (lineItems[qtyCalcIndex]?.qty > 0 ? [lineItems[qtyCalcIndex]?.qty] : null)
      }
      onApply={(total, breakdown) => handleQtyCalcApply(qtyCalcIndex, total, breakdown)}
    />
    </>
  );
}

export default PurchaseCreateModal;
